import { useKeycloak } from "@react-keycloak/web";
import { DashboardFilters } from "../components/DashboardFilters";
import { DashboardList } from "../components/DashboardList";
import { useProjectsPage, useProjectsStore } from "../state/projects";
import { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import { LoadingAnimation } from "../components/LoadingAnimation";

export const ProjectsPage = (
  { showMenu, setShowMenu }:
    { showMenu: boolean, setShowMenu?: React.Dispatch<React.SetStateAction<boolean>>, mapReload?: boolean }
) => {

  const { keycloak: { token } } = useKeycloak();
  const { loadProjectsPage } = useProjectsPage();
  const projectsData = useProjectsStore((state) => state.projects);
  const [sortBy, setSortBy] = useState<string>('name');
  const [riskLevelFilter, setRiskLevelFilter] = useState<string>('all');
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    async function loadProjects() {
      try {
        setIsLoading(true);
        await loadProjectsPage(1);
      } catch (error) {
        toast.error('Failed to load projects', {
          toastId: 'failed-to-load-projects'
        });
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    }
    if (token) {
      loadProjects();
    }
  }, [token]);

  // Function to get project's highest risk level from project plan items
  const getProjectRiskLevel = (project: any): string => {
    if (!project.items || project.items.length === 0) {
      return 'none';
    }
    
    const riskLevels = project.items.map((item: any) => 
      item.risks?.toLowerCase() || 'none'
    );
    
    // Return the highest risk level found in the project
    if (riskLevels.includes('high')) return 'high';
    if (riskLevels.includes('medium')) return 'medium';
    if (riskLevels.includes('low')) return 'low';
    return 'none';
  };

  const sortedAndFilteredProjects = useMemo(() => {
    if (!projectsData) return [];
    
    // Filter by risk level first
    let filtered = [...projectsData];
    if (riskLevelFilter !== 'all') {
      filtered = filtered.filter(project => getProjectRiskLevel(project) === riskLevelFilter);
    }
    
    // Then sort
    const sorted = filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'name_desc':
          return b.name.localeCompare(a.name);
        case 'status':
          return (a.status || '').localeCompare(b.status || '');
        case 'created_date':
          return new Date(b.start_date || '').getTime() - new Date(a.start_date || '').getTime();
        case 'created_date_desc':
          return new Date(a.start_date || '').getTime() - new Date(b.start_date || '').getTime();
        case 'updated_date':
          return new Date(b.end_date || '').getTime() - new Date(a.end_date || '').getTime();
        case 'updated_date_desc':
          return new Date(a.end_date || '').getTime() - new Date(b.end_date || '').getTime();
        default:
          return 0;
      }
    });
    
    return sorted;
  }, [projectsData, sortBy, riskLevelFilter]);
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingAnimation 
          size={150} 
          text="Loading projects..." 
          className="text-center"
        />
      </div>
    );
  }

  return (
    <>
      <DashboardFilters
        setShowMenu={setShowMenu}
        showMenu={showMenu}
        view={'projects'}
        onSortChange={setSortBy}
        onPriorityFilter={setRiskLevelFilter}
        selectedRiskLevel={riskLevelFilter}
      />
      <DashboardList servers={sortedAndFilteredProjects} />
    </>
  );
};
